import { Modal, Stack, Group, Button, Text, List, Alert } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { CompletionWarning } from "../../hooks/useCompletionWarnings";

interface CompletionWarningModalProps {
  opened: boolean;
  warnings: CompletionWarning[];
  caseTypeLabel: string;
  isCompleting: boolean;
  onClose: () => void;
  onCompleteAnyway: () => void;
  onEnterDetails: () => void;
}

/**
 * Soft warning shown at Complete time when fields expected for the derived case type are
 * missing. The operator can complete anyway, or jump to the first missing field.
 */
export default function CompletionWarningModal({
  opened,
  warnings,
  caseTypeLabel,
  isCompleting,
  onClose,
  onCompleteAnyway,
  onEnterDetails,
}: CompletionWarningModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Some details are missing"
      centered
      withinPortal
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
          role="alert"
        >
          This {caseTypeLabel} case is missing details usually provided for its
          type. You can complete anyway, or enter them first.
        </Alert>

        <div>
          <Text size="sm" fw={500} mb={4}>
            Missing fields
          </Text>
          <List size="sm" spacing={4}>
            {warnings.map((warning) => (
              <List.Item key={warning.field}>{warning.label}</List.Item>
            ))}
          </List>
        </div>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={onCompleteAnyway}
            loading={isCompleting}
          >
            Complete anyway
          </Button>
          <Button onClick={onEnterDetails}>Enter details</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
